import { useEffect, useMemo, useState } from 'react'
import { useForm, FormProvider } from 'react-hook-form'
import { useRouter } from 'next/router'
import { postFormRequest, sendFeedbackFitResponse } from '@api/apiCalls'
import Translations from '@helpers/translations'
import { getCookieByName } from '@helpers/session'
import { getAnalyticsCountryCode, getLink } from '@helpers/env'
import { getIsUserLoggedIn } from '@helpers/session/user'
import data from './data'
import styles from './styles.module.scss'
import { z } from 'zod'
import { zodResolver } from '@hookform/resolvers/zod'
import FormGrid from '@components/ui/formElements/form/shared/FormGrid'
import { SubmitButton } from '@components/ui/Button'
import { SubmitButtonState } from '@components/ui/Button/SubmitButton'
import axios from 'axios'
import FeedbackField from './FeedbackField'
import FeedbackStatusMessages from './FeedbackStatusMessages'
import { getBulletLabels, toggleQuestionsVisibility } from './helpers'
import SectionHeader from '@components/ui/SectionHeader'
import { FormContentProps } from './types'

type Payload = {
  source: string
  [key: string]: string | number | boolean
}

export type FormProps = {
  formData: {
    header: {
      copy?: string
      byline?: string[]
      title: string
    }
    requestHandle: string
    handle: string
    content: FormContentProps['data'][]
  }
  onSubmit?: (arg: {
    payload: Payload
    submitForm: (arg: Payload) => void
  }) => void
  onChange?: (values: Record<string, string>) => void
}

const BYLINES = {
  good: Translations.localise('pages.feedback.byline.good'),
  bad: Translations.localise('pages.feedback.byline.bad')
}

const getUpdatedFormData = (fitResponse = '') => {
  const content = data.content.map(entry => {
    if (entry.id === 'email') {
      return { ...entry, required: false, hidden: true }
    }
    return entry
  })
  const byline = fitResponse?.toLowerCase() as keyof typeof BYLINES
  return {
    ...data,
    content,
    header: {
      ...data.header,
      copy: BYLINES[byline]
    }
  }
}

export const SpokeFeedbackSchema = z.object({
  email: z
    .string()
    .trim()
    .optional()
    .transform(v => (v === '' ? undefined : v))
    .refine(
      v => v === undefined || z.string().email().safeParse(v).success,
      'Enter a valid email address'
    ),

  how_likely_are_you_to_recommend: z.string().min(1, 'Required'),
  rate_fit: z.string().min(1, 'Required'),
  how_can_we_improve_fit: z
    .string()
    .trim()
    .optional()
    .transform(v => (v === '' ? undefined : v)),

  rate_delivery_and_customer_service: z.string().min(1, 'Required'),
  new_products: z
    .string()
    .trim()
    .optional()
    .transform(v => (v === '' ? undefined : v)),

  price_to_value: z.string().min(1, 'Required'),
  site_navigation: z.string().min(1, 'Required'),
  find_the_product_you_wanted: z
    .string()
    .trim()
    .optional()
    .transform(v => (v === '' ? undefined : v))
})

export type SpokeFeedbackData = z.infer<typeof SpokeFeedbackSchema>

const defaultFormValues: SpokeFeedbackData = {
  email: '',
  how_likely_are_you_to_recommend: '',
  rate_fit: '',
  how_can_we_improve_fit: '',
  rate_delivery_and_customer_service: '',
  new_products: '',
  price_to_value: '',
  site_navigation: '',
  find_the_product_you_wanted: ''
}

export default function SpokeFeedback() {
  const router = useRouter()
  const { userId, fitResponse } = router.query as {
    userId: string
    fitResponse: string
  }
  const [buttonState, setButtonState] = useState<SubmitButtonState>('ready')
  const [serverError, setServerError] = useState<string | null>(null)
  const [successMessage, setSuccessMessage] = useState<string | null>(null)
  const methods = useForm<SpokeFeedbackData & { customError?: string }>({
    mode: 'onBlur',
    reValidateMode: 'onChange',
    shouldFocusError: true,
    resolver: zodResolver(SpokeFeedbackSchema),
    defaultValues: defaultFormValues
  })

  const { formState, watch, handleSubmit } = methods
  const { errors, isDirty, isValid, isSubmitted } = formState

  const hasSavedFitResponse = getCookieByName('spoke_fit_response')
  const [feedbackFitResponseHasErrors, setFeedbackFitResponseHasErrors] =
    useState(false)

  const [formData, setFormData] = useState<FormProps['formData']>(data)

  const hasUserAndFitResponse = Boolean(userId && fitResponse)
  const hasValidUserAndFitResponse =
    hasUserAndFitResponse && !feedbackFitResponseHasErrors

  const isSubmitDisabled = !isDirty || !isValid || buttonState === 'loading'

  useEffect(() => {
    if (!hasSavedFitResponse && hasUserAndFitResponse) {
      const sendFitFeedback = async () => {
        const response = await sendFeedbackFitResponse({ userId, fitResponse })

        if (response?.error) {
          setFeedbackFitResponseHasErrors(true)
          // reset form data
          setFormData(data)
        } else {
          setFormData(getUpdatedFormData(fitResponse))
        }
      }

      sendFitFeedback()
    } else if (hasUserAndFitResponse) {
      setFormData(getUpdatedFormData(fitResponse))
    }
  }, [userId, hasUserAndFitResponse, fitResponse, hasSavedFitResponse])

  const watchedValues = watch()

  const visibleContent = useMemo(
    () =>
      toggleQuestionsVisibility(
        watchedValues as Record<string, string>,
        formData.content
      ),
    [watchedValues, formData.content]
  )

  const handleOnSubmit = async (values: SpokeFeedbackData) => {
    setServerError(null)

    const emailField = formData.content.find(field => field.id === 'email')
    const isEmailVisible = emailField ? !emailField.hidden : true

    if (isEmailVisible && !values.email) {
      methods.setError('email', {
        type: 'manual',
        message: 'Enter a valid email address'
      })
      return
    }

    const defaultPayload = {
      ...values,
      store_location: getAnalyticsCountryCode(),
      logged_in: getIsUserLoggedIn(),
      source: getAnalyticsCountryCode().toUpperCase()
    }

    const formPayload = hasValidUserAndFitResponse
      ? { ...defaultPayload, userId, fit_response: fitResponse }
      : defaultPayload

    try {
      setButtonState('loading')
      await postFormRequest(formPayload, formData.requestHandle)
      setSuccessMessage('Feedback form sent successfully')

      methods.reset(defaultFormValues)

      setFormData(
        hasUserAndFitResponse ? getUpdatedFormData(fitResponse) : data
      )
      setButtonState('ready')

      window.location.href = getLink('/')
    } catch (e) {
      const error = e as Error
      setButtonState('ready')

      const serverMessage =
        (axios.isAxiosError(error) &&
          error.response &&
          error?.response?.data?.message) ||
        error?.message ||
        'Something went wrong while submitting the form. Please try again.'

      setServerError(serverMessage)
    }
  }

  const hasFieldErrors = Object.keys(errors).length > 0

  return (
    <div className={styles.Form}>
      <SectionHeader title="Sixty second survey" variant="left_aligned" />

      <FormProvider {...methods}>
        <p className={styles.Form__copy}>{formData.header.copy}</p>

        <form
          onSubmit={handleSubmit(handleOnSubmit)}
          noValidate
          className={styles.Form__content}
        >
          <div className={styles.Form__container}>
            <FormGrid>
              {visibleContent
                .filter(field => !field.hidden)
                .map(field => (
                  <FeedbackField
                    key={field.id}
                    field={field}
                    getBulletLabels={getBulletLabels}
                  />
                ))}
            </FormGrid>
          </div>

          <FeedbackStatusMessages
            serverError={serverError}
            successMessage={successMessage}
            hasFieldErrors={hasFieldErrors}
            isSubmitted={isSubmitted}
          />

          <div className={styles.Form__button}>
            <SubmitButton
              state={buttonState}
              disabled={isSubmitDisabled}
              type="submit"
              fullWidth="never"
              className={styles.submit}
            >
              Submit
            </SubmitButton>
          </div>
        </form>
      </FormProvider>
    </div>
  )
}
